import { PharmacyModal } from "@/components/PharmacySearch";
import {
  PharmacyInvalidReason,
  usePreferredPharmacy,
} from "@/components/PharmacySearch/network/usePreferredPharmacy";
import { BodyText, Callout, Skeleton } from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { useBatchApplyPharmacy } from "@/features/Charting/components/network/useBatchApplyPharmacy";
import { EditableRecordButton } from "@/features/Patient/MedicalRecords/components/EditableRecordButton";
import { trackEvent } from "@/lib/tracking";
import type { Location } from "@curai/js-fhir-tools";
import { useSearch } from "@tanstack/react-router";
import { Fragment, useState } from "react";

export const PharmacyEditor = ({ patientId }: { patientId: string }) => {
  const { encounterId } = useSearch({ from: "/patient/$patientId" });
  const [open, setOpen] = useState(false);

  const {
    data: { pharmacy, pharmacyInvalidReason } = {},
    isLoading,
    isError,
  } = usePreferredPharmacy({ patientId });

  const batchApplyPharmacy = useBatchApplyPharmacy({
    patientId,
    encounterId: encounterId as string,
  });

  const handleApplyToAllMedications = async (pharmacy: Location) => {
    await batchApplyPharmacy.mutateAsync({
      pharmacyId: pharmacy.identifier?.[0]?.value || "",
    });
    setOpen(false);
  };

  if (isError) {
    return (
      <Callout
        className="w-full cursor-pointer"
        variant="danger"
        title="Unable to load pharmacy"
        description="Please try again later."
      />
    );
  }

  if (isLoading) {
    return (
      <div className="py-2 px-3">
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <Fragment>
      <PharmacyModal
        open={open}
        onOpenChange={setOpen}
        patientId={patientId}
        applyToAllMedications={
          encounterId ? handleApplyToAllMedications : undefined
        }
      />
      <EditableRecordButton
        title="Pharmacy"
        onClick={() => {
          trackEvent(TRACKERS.EDIT_PHARMACY);
          setOpen(true);
        }}
      >
        {pharmacy ? (
          <div className="flex flex-col">
            <BodyText size="sm">{pharmacy.name}</BodyText>
            {pharmacy.address?.line?.map((line) => (
              <BodyText key={line} size="sm">
                {line}
              </BodyText>
            ))}
            <BodyText size="sm">
              {pharmacy.address?.city}, {pharmacy.address?.state}{" "}
              {pharmacy.address?.postalCode}
            </BodyText>
            {pharmacyInvalidReason ===
              PharmacyInvalidReason.MISSING_VENDOR_ID && (
              <Callout
                title="Pharmacy Not Found"
                description="This pharmacy was not found in Dosespot. Click to search for a new pharmacy."
                variant="danger"
              />
            )}
          </div>
        ) : (
          <BodyText size="sm" italic>
            No preferred pharmacy set.
          </BodyText>
        )}
      </EditableRecordButton>
    </Fragment>
  );
};
